import { useEffect } from 'react';
import { useOfflineStatus } from '@/hooks/useOfflineStatus';

// Endpoints "hot" que casi todo administrador va a consultar en su sesión.
// El Service Worker (NetworkFirst con stale fallback) los cacheará al pasar.
// Marcados como GET puros; nunca incluir POST/PUT.
const HOT_ENDPOINTS = [
  // Agenda del día — el rango es "±3 días" para cubrir navegación adjacent.
  () => {
    const today = new Date();
    const start = new Date(today);
    start.setDate(today.getDate() - 1);
    const end = new Date(today);
    end.setDate(today.getDate() + 6);
    const iso = (d) => d.toISOString().slice(0, 10);
    return `/api/appointments?start_date=${iso(start)}&end_date=${iso(end)}&page_size=500`;
  },
  // Lista de pacientes (primera página) — la topbar de búsqueda universal la usa.
  () => '/api/patients?page=1&page_size=50',
  // Presupuestos activos — hot path recién trabajado.
  () => '/api/surgery-budgets?page=1&page_size=50',
  // Cirugías próximas.
  () => '/api/surgeries?page=1&page_size=50',
  // Catálogos ligeros — cambian rara vez pero son referenciados en muchas vistas.
  () => '/api/locations',
  () => '/api/insurances',
  () => '/api/doctors',
];

// Flag global para no repetir en la misma pestaña (aunque cambies de página).
let PREFETCHED_THIS_SESSION = false;

/**
 * useSmartPrefetch — al montarse en un layout autenticado, dispara en
 * background un `fetch()` a los endpoints más consultados para que el
 * Service Worker + caché HTTP los tengan listos.
 *
 * Reglas de cortesía:
 *   • Sólo se ejecuta 1 vez por pestaña (flag `PREFETCHED_THIS_SESSION`).
 *   • Espera a que el navegador esté idle (~1500ms tras mount).
 *   • Aborta si el usuario está offline o en red lenta (2G) — no gastamos
 *     datos móviles del móvil.
 *   • Cada fetch va con `credentials: 'same-origin'` para reutilizar la
 *     cookie/token de sesión, y `priority: 'low'` para no competir con
 *     los recursos que sí necesita el usuario ahora.
 */
export function useSmartPrefetch() {
  const { isOffline, isSlow } = useOfflineStatus();

  useEffect(() => {
    if (PREFETCHED_THIS_SESSION) return;
    if (isOffline || isSlow) return;
    if (typeof window === 'undefined') return;

    const run = () => {
      if (PREFETCHED_THIS_SESSION) return;
      PREFETCHED_THIS_SESSION = true;
      HOT_ENDPOINTS.forEach((buildUrl) => {
        try {
          const url = buildUrl();
          // fetch con prioridad baja (Chrome/Edge la respetan; Safari la ignora).
          fetch(url, {
            method: 'GET',
            credentials: 'same-origin',
            cache: 'default',
            priority: 'low',
          }).catch(() => { /* silencioso — es sólo un warmup */ });
        } catch (_) { /* ignore */ }
      });
    };

    // Espera al idle del navegador para no competir con la primera pintura.
    const ric = window.requestIdleCallback || ((fn) => setTimeout(fn, 1500));
    const cic = window.cancelIdleCallback || clearTimeout;
    const handle = ric(run, { timeout: 4000 });
    return () => cic(handle);
  }, [isOffline, isSlow]);
}
